// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useState, useEffect, useCallback, useRef } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Grid from '@mui/material/Grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
import HubIcon from '@mui/icons-material/Hub';
import { remoteGet, remotePut } from '../utils/remoteApi';
import { useTheme } from '../context/ThemeContext';

const FIELD_LABELS = {
  PrivateKey: 'Private Key',
  Address: 'Address',
  DNS: 'DNS',
  MTU: 'MTU',
  PublicKey: 'Public Key',
  Endpoint: 'Endpoint',
  AllowedIPs: 'Allowed IPs',
  PersistentKeepalive: 'Persistent Keepalive',
};

const configToText = (config) => {
  if (!config) return '';
  const lines = [];
  const iface = config.interface || {};
  lines.push('[Interface]');
  for (const [key, val] of Object.entries(iface)) {
    if (val != null && val !== '')
      lines.push(`${key} = ${Array.isArray(val) ? val.join(', ') : val}`);
  }
  for (const peer of config.peers || []) {
    lines.push('');
    lines.push('[Peer]');
    for (const [key, val] of Object.entries(peer)) {
      if (val != null && val !== '')
        lines.push(`${key} = ${Array.isArray(val) ? val.join(', ') : val}`);
    }
  }
  return lines.join('\n') + '\n';
};

const parseConfFile = (text) => {
  const lines = text.split(/\r?\n/);
  const config = { interface: {}, peers: [] };
  let section = null;
  let currentPeer = null;
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) continue;
    if (trimmed.toLowerCase() === '[interface]') {
      section = 'interface';
      continue;
    }
    if (trimmed.toLowerCase() === '[peer]') {
      if (currentPeer) config.peers.push(currentPeer);
      currentPeer = {};
      section = 'peer';
      continue;
    }
    const eqIdx = trimmed.indexOf('=');
    if (eqIdx === -1) continue;
    const key = trimmed.slice(0, eqIdx).trim();
    const val = trimmed.slice(eqIdx + 1).trim();
    if (section === 'interface') config.interface[key] = val;
    else if (section === 'peer' && currentPeer) currentPeer[key] = val;
  }
  if (currentPeer) config.peers.push(currentPeer);
  if (config.interface.MTU)
    config.interface.MTU = parseInt(config.interface.MTU, 10) || config.interface.MTU;
  if (config.interface.ListenPort)
    config.interface.ListenPort =
      parseInt(config.interface.ListenPort, 10) || config.interface.ListenPort;
  for (const peer of config.peers) {
    if (peer.PersistentKeepalive)
      peer.PersistentKeepalive = parseInt(peer.PersistentKeepalive, 10) || peer.PersistentKeepalive;
  }
  return config;
};

// Two-step confirmation dialog for dangerous operations
const DangerConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  loading,
}) => {
  const [typed, setTyped] = useState('');
  const requiredPhrase = 'I understand';

  useEffect(() => {
    if (!open) setTyped('');
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: 'error.main',
        }}
      >
        <WarningAmberIcon color="error" />
        {title}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{message}</DialogContentText>
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Connectivity Warning</AlertTitle>
          If the dashboard communicates with this Stargate over the wg0 tunnel and you apply
          incorrect credentials with restart enabled,{' '}
          <strong>you will lose access to the device</strong>. A backup is created automatically on
          the device before each change.
        </Alert>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Type <strong>I understand</strong> to confirm:
        </Typography>
        <TextField
          size="small"
          fullWidth
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder="I understand"
          autoFocus
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="error"
          disabled={typed !== requiredPhrase || loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {confirmText || 'Apply Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// One label / value row with a code-styled value + copy (and optional mask toggle).
const FieldRow = ({
  label,
  rawValue,
  masked,
  secret,
  showSecret,
  onToggleSecret,
  isDarkMode,
  last,
}) => {
  const [copied, setCopied] = useState(false);
  const text = rawValue == null || rawValue === '' ? '(not set)' : String(rawValue);
  const isEmpty = rawValue == null || rawValue === '';
  const display =
    secret && !showSecret && !isEmpty ? `${text.slice(0, 6)}…${text.slice(-4)}` : text;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* clipboard blocked — non-fatal */
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.25,
        borderBottom: last ? 'none' : '1px solid',
        borderColor: 'divider',
        transition: 'background-color 120ms ease',
        '&:hover': {
          backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
        },
        '&:hover .wg-row-actions': { opacity: 1 },
      }}
    >
      <Typography
        sx={{
          width: 150,
          flexShrink: 0,
          fontSize: '0.7rem',
          fontWeight: 700,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: isDarkMode ? 'grey.500' : 'grey.600',
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          fontFamily: 'monospace',
          fontSize: '0.8rem',
          color: isEmpty ? 'text.disabled' : 'text.primary',
          wordBreak: 'break-all',
          fontStyle: isEmpty ? 'italic' : 'normal',
        }}
      >
        {display}
      </Box>
      <Box
        className="wg-row-actions"
        sx={{
          display: 'flex',
          gap: 0.25,
          flexShrink: 0,
          opacity: { xs: 1, md: 0.35 },
        }}
      >
        {secret && (
          <Tooltip title={showSecret ? 'Hide' : 'Reveal'}>
            <IconButton size="small" onClick={onToggleSecret} sx={{ color: 'text.secondary' }}>
              {showSecret ? (
                <VisibilityOffIcon sx={{ fontSize: 17 }} />
              ) : (
                <VisibilityIcon sx={{ fontSize: 17 }} />
              )}
            </IconButton>
          </Tooltip>
        )}
        {!isEmpty && (
          <Tooltip title={copied ? 'Copied!' : 'Copy'}>
            <IconButton
              size="small"
              onClick={copy}
              sx={{ color: copied ? 'success.main' : 'text.secondary' }}
            >
              {copied ? (
                <CheckIcon sx={{ fontSize: 17 }} />
              ) : (
                <ContentCopyIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

// A titled section card ([Interface] / [Peer]) with an accent icon header.
const ConfigSection = ({ icon, title, accent, isDarkMode, children }) => (
  <Box sx={{ mb: 2.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: `${accent}1f`,
          color: accent,
        }}
      >
        {icon}
      </Box>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: '0.85rem',
          letterSpacing: '0.02em',
          color: 'text.primary',
        }}
      >
        {title}
      </Typography>
    </Box>
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        borderLeft: `3px solid ${accent}`,
        backgroundColor: isDarkMode ? 'rgba(255,255,255,0.02)' : '#fcfdff',
      }}
    >
      {children}
    </Paper>
  </Box>
);

// Read-only display of the current config
const ConfigDisplay = ({ config, isDarkMode, showPrivateKey, onTogglePrivateKey }) => {
  if (!config) return null;
  const iface = Object.entries(config.interface || {});
  const peers = config.peers || [];
  const ifaceAccent = isDarkMode ? '#4fc3f7' : '#1565c0';
  const peerAccent = isDarkMode ? '#81c784' : '#2e7d32';

  return (
    <Box>
      <ConfigSection
        icon={<SettingsEthernetIcon sx={{ fontSize: 17 }} />}
        title="Interface"
        accent={ifaceAccent}
        isDarkMode={isDarkMode}
      >
        {iface.map(([key, val], i) => (
          <FieldRow
            key={key}
            label={FIELD_LABELS[key] || key}
            rawValue={Array.isArray(val) ? val.join(', ') : val}
            secret={key === 'PrivateKey'}
            showSecret={showPrivateKey}
            onToggleSecret={onTogglePrivateKey}
            isDarkMode={isDarkMode}
            last={i === iface.length - 1}
          />
        ))}
      </ConfigSection>

      {peers.map((peer, idx) => {
        const entries = Object.entries(peer);
        return (
          <ConfigSection
            key={idx}
            icon={<HubIcon sx={{ fontSize: 16 }} />}
            title={peers.length > 1 ? `Peer #${idx + 1}` : 'Peer'}
            accent={peerAccent}
            isDarkMode={isDarkMode}
          >
            {entries.map(([key, val], i) => (
              <FieldRow
                key={key}
                label={FIELD_LABELS[key] || key}
                rawValue={Array.isArray(val) ? val.join(', ') : val}
                secret={key === 'PresharedKey'}
                showSecret={showPrivateKey}
                onToggleSecret={onTogglePrivateKey}
                isDarkMode={isDarkMode}
                last={i === entries.length - 1}
              />
            ))}
          </ConfigSection>
        );
      })}
    </Box>
  );
};

// Edit form for interface fields
const InterfaceEditForm = ({ iface, onChange, isDarkMode }) => {
  const handleChange = (key, value) => {
    onChange({ ...iface, [key]: value });
  };

  return (
    <Box>
      <Typography
        variant="subtitle2"
        sx={{
          mb: 1.5,
          fontWeight: 700,
          color: isDarkMode ? 'primary.light' : '#1565c0',
        }}
      >
        [Interface]
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Private Key"
            value={iface.PrivateKey || ''}
            onChange={(e) => handleChange('PrivateKey', e.target.value)}
            fullWidth
            size="small"
            required
            type="password"
            InputProps={{
              sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Address (CIDR)"
            value={iface.Address || ''}
            onChange={(e) => handleChange('Address', e.target.value)}
            fullWidth
            size="small"
            placeholder="e.g. 10.2.2.27/32"
            InputProps={{
              sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="DNS"
            value={iface.DNS || ''}
            onChange={(e) => handleChange('DNS', e.target.value)}
            fullWidth
            size="small"
            placeholder="e.g. 10.2.255.241"
            InputProps={{
              sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="MTU"
            value={iface.MTU || ''}
            onChange={(e) => handleChange('MTU', e.target.value)}
            fullWidth
            size="small"
            placeholder="e.g. 1100"
            InputProps={{
              sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

// Edit form for a single peer
const PeerEditForm = ({ peer, index, onChange, onRemove, isDarkMode }) => {
  const handleChange = (key, value) => {
    onChange(index, { ...peer, [key]: value });
  };

  const handleAllowedIPsChange = (value) => {
    // Store as comma-separated string during editing, will be split on save
    handleChange('AllowedIPs', value);
  };

  const allowedIPsValue = Array.isArray(peer.AllowedIPs)
    ? peer.AllowedIPs.join(', ')
    : peer.AllowedIPs || '';

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            color: isDarkMode ? 'primary.light' : '#1565c0',
            flex: 1,
          }}
        >
          [Peer] #{index + 1}
        </Typography>
        <Tooltip title="Remove this peer">
          <IconButton size="small" color="error" onClick={() => onRemove(index)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Public Key"
            value={peer.PublicKey || ''}
            onChange={(e) => handleChange('PublicKey', e.target.value)}
            fullWidth
            size="small"
            required
            InputProps={{
              sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Endpoint"
            value={peer.Endpoint || ''}
            onChange={(e) => handleChange('Endpoint', e.target.value)}
            fullWidth
            size="small"
            placeholder="e.g. 44.229.176.109:60000"
            InputProps={{
              sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Persistent Keepalive"
            value={peer.PersistentKeepalive || ''}
            onChange={(e) => handleChange('PersistentKeepalive', e.target.value)}
            fullWidth
            size="small"
            placeholder="e.g. 25"
            InputProps={{
              sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Allowed IPs (comma-separated)"
            value={allowedIPsValue}
            onChange={(e) => handleAllowedIPsChange(e.target.value)}
            fullWidth
            size="small"
            placeholder="e.g. 10.2.0.0/16, 10.17.0.0/16"
            InputProps={{
              sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

const StargateHostWireguardConfig = ({ ip, apiKey }) => {
  const { isDarkMode } = useTheme();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editIface, setEditIface] = useState({});
  const [editPeers, setEditPeers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [restartAfterSave, setRestartAfterSave] = useState(false);
  const [showPrivateKey, setShowPrivateKey] = useState(false);
  const [lastBackup, setLastBackup] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [pendingUpload, setPendingUpload] = useState(null);
  const [uploadConfirmOpen, setUploadConfirmOpen] = useState(false);
  const [uploadSaving, setUploadSaving] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef();

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await remoteGet(ip, apiKey, '/api/vpn/host-config');
      setConfig(data);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Failed to load host WireGuard config';
      setError(msg);
      showSnackbar(msg, 'error');
    }
    setLoading(false);
  }, [ip, apiKey]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const startEditing = () => {
    if (!config) return;
    setEditIface({ ...config.interface });
    setEditPeers(
      (config.peers || []).map((p) => ({
        ...p,
        AllowedIPs: Array.isArray(p.AllowedIPs) ? [...p.AllowedIPs] : p.AllowedIPs,
      }))
    );
    setEditing(true);
    setRestartAfterSave(false);
  };

  const cancelEditing = () => {
    setEditing(false);
    setEditIface({});
    setEditPeers([]);
  };

  const handlePeerChange = (index, updatedPeer) => {
    setEditPeers((prev) => prev.map((p, i) => (i === index ? updatedPeer : p)));
  };

  const handleRemovePeer = (index) => {
    setEditPeers((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddPeer = () => {
    setEditPeers((prev) => [
      ...prev,
      {
        PublicKey: '',
        Endpoint: '',
        AllowedIPs: '',
        PersistentKeepalive: '25',
      },
    ]);
  };

  const handleSaveClick = (withRestart) => {
    // Validate required fields
    if (!editIface.PrivateKey || !editIface.PrivateKey.trim()) {
      showSnackbar('Private Key is required', 'error');
      return;
    }
    for (let i = 0; i < editPeers.length; i++) {
      if (!editPeers[i].PublicKey || !editPeers[i].PublicKey.trim()) {
        showSnackbar(`Peer #${i + 1} requires a Public Key`, 'error');
        return;
      }
    }
    setRestartAfterSave(withRestart);
    setConfirmOpen(true);
  };

  const handleConfirmedSave = async () => {
    setSaving(true);
    try {
      // Prepare peers - convert AllowedIPs strings to arrays
      const preparedPeers = editPeers.map((p) => {
        const peer = { ...p };
        if (typeof peer.AllowedIPs === 'string') {
          const ips = peer.AllowedIPs.split(',')
            .map((s) => s.trim())
            .filter(Boolean);
          peer.AllowedIPs = ips.length > 1 ? ips : ips[0] || '';
        }
        // Convert numeric strings for PersistentKeepalive
        if (peer.PersistentKeepalive) {
          peer.PersistentKeepalive =
            parseInt(peer.PersistentKeepalive, 10) || peer.PersistentKeepalive;
        }
        return peer;
      });

      // Prepare interface - convert MTU to int if numeric
      const preparedIface = { ...editIface };
      if (preparedIface.MTU) {
        preparedIface.MTU = parseInt(preparedIface.MTU, 10) || preparedIface.MTU;
      }

      const payload = {
        interface: preparedIface,
        peers: preparedPeers,
        restart: restartAfterSave,
      };

      const result = await remotePut(ip, apiKey, '/api/vpn/host-config', payload);

      if (result.backup) {
        setLastBackup(result.backup);
      }

      if (result.config) {
        setConfig(result.config);
      } else {
        await fetchConfig();
      }

      setEditing(false);
      setConfirmOpen(false);

      let msg = 'Host WireGuard config updated successfully.';
      if (result.backup) {
        msg += ` Backup: ${result.backup}`;
      }
      if (result.restart?.restarted) {
        msg += ' WireGuard restarted.';
      }
      if (result.restart?.error) {
        showSnackbar(`Config saved but restart failed: ${result.restart.error}`, 'warning');
      } else {
        showSnackbar(msg, 'success');
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Failed to save host WireGuard config';
      showSnackbar(msg, 'error');
    }
    setSaving(false);
  };

  const handleDownload = () => {
    const text = configToText(config);
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'wg0.conf';
    a.click();
    URL.revokeObjectURL(url);
    showSnackbar('Downloaded wg0.conf');
  };

  const handleFileSelect = async (file) => {
    if (!file || !file.name.endsWith('.conf')) {
      showSnackbar('Only .conf files are accepted.', 'warning');
      return;
    }
    try {
      const text = await file.text();
      const parsed = parseConfFile(text);
      if (!parsed.interface.PrivateKey) {
        showSnackbar('Invalid config: missing PrivateKey in [Interface]', 'error');
        return;
      }
      setPendingUpload({ config: parsed, filename: file.name });
      setRestartAfterSave(false);
      setUploadConfirmOpen(true);
    } catch (e) {
      showSnackbar('Failed to parse config file.', 'error');
    }
  };

  const handleConfirmedUpload = async () => {
    if (!pendingUpload) return;
    setUploadSaving(true);
    try {
      const payload = {
        interface: pendingUpload.config.interface,
        peers: pendingUpload.config.peers,
        restart: restartAfterSave,
      };
      const result = await remotePut(ip, apiKey, '/api/vpn/host-config', payload);
      if (result.backup) setLastBackup(result.backup);
      if (result.config) setConfig(result.config);
      else await fetchConfig();

      setUploadConfirmOpen(false);
      setPendingUpload(null);

      let msg = `Replaced host config with ${pendingUpload.filename}.`;
      if (result.backup) msg += ` Backup: ${result.backup}`;
      if (result.restart?.restarted) msg += ' WireGuard restarted.';
      if (result.restart?.error)
        showSnackbar(`Config replaced but restart failed: ${result.restart.error}`, 'warning');
      else showSnackbar(msg);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Failed to upload config';
      showSnackbar(msg, 'error');
    }
    setUploadSaving(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.length > 0) handleFileSelect(e.dataTransfer.files[0]);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  return (
    <Card
      elevation={0}
      sx={{
        // Rendered inside the widget-stack card, which already draws the
        // border/radius — stay borderless and full-width to avoid double chrome.
        background: 'transparent',
        borderRadius: 3,
        boxShadow: 'none',
        width: '100%',
        p: 0,
      }}
    >
      <CardHeader
        avatar={
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: isDarkMode ? 'rgba(33, 150, 243, 0.14)' : '#e3f2fd',
              color: isDarkMode ? '#4fc3f7' : '#1565c0',
            }}
          >
            <VpnKeyIcon sx={{ fontSize: 22 }} />
          </Box>
        }
        title={
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="h6"
                sx={{ color: 'text.primary', fontWeight: 700, fontSize: 20 }}
              >
                Host WireGuard Config
              </Typography>
              <Chip
                label="wg0.conf"
                size="small"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  backgroundColor: isDarkMode ? 'rgba(138, 148, 168, 0.15)' : '#e3f2fd',
                  color: isDarkMode ? 'primary.light' : '#1565c0',
                }}
              />
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: isDarkMode ? 'grey.400' : 'grey.600',
                mt: 0.5,
                fontWeight: 400,
              }}
            >
              The device's host tunnel configuration
            </Typography>
          </Box>
        }
        action={
          !editing && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75, mr: 0.5 }}>
              <Tooltip title="Download wg0.conf">
                <span>
                  <IconButton onClick={handleDownload} disabled={loading || !config} size="small">
                    <FileDownloadIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Refresh config">
                <IconButton onClick={fetchConfig} disabled={loading} size="small">
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Button
                onClick={startEditing}
                disabled={loading || !config}
                size="small"
                variant="outlined"
                startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                sx={{ ml: 0.5, textTransform: 'none', fontWeight: 600 }}
              >
                Edit
              </Button>
            </Box>
          )
        }
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: isDarkMode ? 'rgba(138, 148, 168, 0.06)' : '#ffffff',
          py: 2,
          // Match the host card's rounded corners (we render borderless inside it).
          borderRadius: '12px 12px 0 0',
        }}
      />

      <CardContent sx={{ pb: 3 }}>
        {/* Slim inline caution — the full warning lives in the confirm dialogs
            where the dangerous action actually happens. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.25,
            px: 1.75,
            py: 1.25,
            mb: 2.5,
            borderRadius: 2,
            border: '1px solid',
            borderColor: isDarkMode ? 'rgba(255, 167, 38, 0.28)' : 'rgba(230, 81, 0, 0.3)',
            backgroundColor: isDarkMode ? 'rgba(255, 167, 38, 0.07)' : 'rgba(255, 167, 38, 0.08)',
          }}
        >
          <WarningAmberIcon
            sx={{ fontSize: 18, mt: '2px', color: isDarkMode ? '#ffb74d' : '#e65100' }}
          />
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.55 }}>
            <Box
              component="span"
              sx={{ fontWeight: 700, color: isDarkMode ? '#ffb74d' : '#e65100' }}
            >
              Changes here can disconnect this Stargate from the dashboard.
            </Box>{' '}
            Keep an alternative access path (SSH or physical) before saving. A backup is created on
            the device before every change.
          </Typography>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Skeleton variant="rounded" height={210} animation="wave" sx={{ borderRadius: 2 }} />
            <Skeleton variant="rounded" height={210} animation="wave" sx={{ borderRadius: 2 }} />
          </Box>
        )}

        {error && !loading && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>Error</AlertTitle>
            {error}
          </Alert>
        )}

        {!loading && !error && config && !editing && (
          <ConfigDisplay
            config={config}
            isDarkMode={isDarkMode}
            showPrivateKey={showPrivateKey}
            onTogglePrivateKey={() => setShowPrivateKey((v) => !v)}
          />
        )}

        {lastBackup && !editing && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 1.5,
              fontFamily: 'monospace',
              color: 'text.secondary',
            }}
          >
            Last backup: {lastBackup}
          </Typography>
        )}

        {/* Replace-config drop strip — a rare operation, so it sits compact at
            the bottom instead of dominating the card. */}
        {!editing && !loading && (
          <Box sx={{ mt: 3 }}>
            <Typography
              sx={{
                fontSize: '0.68rem',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: isDarkMode ? 'grey.500' : 'grey.600',
                mb: 0.75,
              }}
            >
              Replace entire config
            </Typography>
            <Box
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.5,
                border: '1.5px dashed',
                borderColor: dragActive ? 'primary.main' : 'divider',
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'border-color 150ms ease, background-color 150ms ease',
                backgroundColor: dragActive ? 'rgba(33, 150, 243, 0.08)' : 'transparent',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: isDarkMode ? 'rgba(33, 150, 243, 0.05)' : 'rgba(33, 150, 243, 0.04)',
                },
              }}
            >
              <input
                type="file"
                accept=".conf"
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={(e) => {
                  if (e.target.files[0]) handleFileSelect(e.target.files[0]);
                  e.target.value = '';
                }}
              />
              <CloudUploadIcon
                sx={{
                  fontSize: 24,
                  color: dragActive ? 'primary.main' : 'text.secondary',
                  flexShrink: 0,
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                  Drop a <code>.conf</code> file here, or click to browse
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Overwrites wg0.conf on the device — you'll confirm before anything is applied.
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                sx={{ pointerEvents: 'none', flexShrink: 0, textTransform: 'none' }}
              >
                Browse
              </Button>
            </Box>
          </Box>
        )}

        {editing && (
          <Box>
            <InterfaceEditForm iface={editIface} onChange={setEditIface} isDarkMode={isDarkMode} />

            <Divider sx={{ my: 2 }} />

            {editPeers.map((peer, idx) => (
              <PeerEditForm
                key={idx}
                peer={peer}
                index={idx}
                onChange={handlePeerChange}
                onRemove={handleRemovePeer}
                isDarkMode={isDarkMode}
              />
            ))}

            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddPeer}
              sx={{ mb: 2 }}
            >
              Add Peer
            </Button>

            <Divider sx={{ my: 2 }} />

            <Box
              sx={{
                display: 'flex',
                gap: 1,
                justifyContent: 'flex-end',
                flexWrap: 'wrap',
              }}
            >
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={cancelEditing}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={() => handleSaveClick(false)}
                disabled={saving}
              >
                Save (No Restart)
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<SaveIcon />}
                onClick={() => handleSaveClick(true)}
                disabled={saving}
              >
                Save & Restart WireGuard
              </Button>
            </Box>
          </Box>
        )}
      </CardContent>

      <DangerConfirmDialog
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setSaving(false);
        }}
        onConfirm={handleConfirmedSave}
        title={restartAfterSave ? 'Save & Restart WireGuard?' : 'Save Host Config?'}
        message={
          restartAfterSave
            ? 'This will overwrite wg0.conf and restart the WireGuard interface. The Stargate will briefly lose tunnel connectivity. If the new config is invalid, you may lose access to this device entirely.'
            : 'This will overwrite wg0.conf on the device. The new config will take effect on the next WireGuard restart or device reboot.'
        }
        confirmText={restartAfterSave ? 'Save & Restart' : 'Save Config'}
        loading={saving}
      />

      <DangerConfirmDialog
        open={uploadConfirmOpen}
        onClose={() => {
          setUploadConfirmOpen(false);
          setPendingUpload(null);
          setUploadSaving(false);
        }}
        onConfirm={handleConfirmedUpload}
        title={`Replace host config with ${pendingUpload?.filename || 'uploaded file'}?`}
        message={`This will completely overwrite wg0.conf with the contents of ${pendingUpload?.filename || 'the uploaded file'}. The new config has ${pendingUpload?.config?.peers?.length || 0} peer(s).`}
        confirmText="Replace Config"
        loading={uploadSaving}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <MuiAlert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Card>
  );
};

export default StargateHostWireguardConfig;
